/**
 * Keyboard Shortcuts Hook
 * 
 * Provides a standardized way to register and handle keyboard shortcuts throughout the app.
 */

import { useEffect, useCallback, useRef } from 'react'

export type KeyboardModifier = 'ctrl' | 'shift' | 'alt' | 'meta'
export type KeyboardKey = string

export interface KeyboardShortcut {
  /**
   * The key combination (e.g., 'k', 'Enter', 'Escape')
   */
  key: KeyboardKey
  
  /**
   * Modifier keys (Ctrl, Shift, Alt, Meta)
   */
  modifiers?: KeyboardModifier[]
  
  /**
   * Callback function to execute when shortcut is triggered
   */
  handler: (event: KeyboardEvent) => void
  
  /**
   * Description of what the shortcut does (for help menu)
   */
  description?: string
  
  /**
   * Whether to prevent default browser behavior
   */
  preventDefault?: boolean
  
  /**
   * Whether to stop event propagation
   */
  stopPropagation?: boolean
  
  /**
   * Whether the shortcut is enabled (default: true)
   */
  enabled?: boolean
}

interface UseKeyboardShortcutsOptions {
  /**
   * Whether shortcuts are enabled globally
   */
  enabled?: boolean
  
  /**
   * Element to attach listeners to (default: document)
   */
  target?: HTMLElement | Document
}

/**
 * Hook to register keyboard shortcuts
 * 
 * @param shortcuts - Array of keyboard shortcuts to register
 * @param options - Configuration options
 * 
 * @example
 * ```tsx
 * useKeyboardShortcuts([
 *   {
 *     key: 'k',
 *     modifiers: ['ctrl'],
 *     handler: () => setSearchOpen(true),
 *     description: 'Open search'
 *   },
 *   {
 *     key: 'Escape',
 *     handler: () => setSearchOpen(false),
 *     description: 'Close search'
 *   }
 * ])
 * ```
 */
export function useKeyboardShortcuts(
  shortcuts: KeyboardShortcut[],
  options: UseKeyboardShortcutsOptions = {}
) {
  const { enabled = true, target = document } = options
  const shortcutsRef = useRef(shortcuts)

  // Update shortcuts ref when shortcuts change
  useEffect(() => {
    shortcutsRef.current = shortcuts
  }, [shortcuts])

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return

    // Check each shortcut
    for (const shortcut of shortcutsRef.current) {
      if (shortcut.enabled === false) continue

      const { key, modifiers = [], handler, preventDefault = true, stopPropagation = false } = shortcut

      // Check if key matches
      const keyMatches = event.key === key || event.code === key

      if (!keyMatches) continue

      // Check modifiers
      const ctrlPressed = event.ctrlKey || event.metaKey
      const shiftPressed = event.shiftKey
      const altPressed = event.altKey
      const metaPressed = event.metaKey

      const requiresCtrl = modifiers.includes('ctrl')
      const requiresShift = modifiers.includes('shift')
      const requiresAlt = modifiers.includes('alt')
      const requiresMeta = modifiers.includes('meta')

      // Check if all required modifiers are pressed
      const ctrlMatch = requiresCtrl ? ctrlPressed : !requiresCtrl || !ctrlPressed
      const shiftMatch = requiresShift ? shiftPressed : !requiresShift || !shiftPressed
      const altMatch = requiresAlt ? altPressed : !requiresAlt || !altPressed
      const metaMatch = requiresMeta ? metaPressed : !requiresMeta || !metaPressed

      // Also check that no unexpected modifiers are pressed (unless they're required)
      const noExtraCtrl = !requiresCtrl ? !ctrlPressed : true
      const noExtraShift = !requiresShift ? !shiftPressed : true
      const noExtraAlt = !requiresAlt ? !altPressed : true
      const noExtraMeta = !requiresMeta ? !metaPressed : true

      if (
        keyMatches &&
        ctrlMatch &&
        shiftMatch &&
        altMatch &&
        metaMatch &&
        noExtraCtrl &&
        noExtraShift &&
        noExtraAlt &&
        noExtraMeta
      ) {
        if (preventDefault) {
          event.preventDefault()
        }
        if (stopPropagation) {
          event.stopPropagation()
        }
        handler(event)
        break // Only trigger one shortcut per keypress
      }
    }
  }, [enabled])

  useEffect(() => {
    if (!enabled) return

    const handler = handleKeyDown as EventListener
    target.addEventListener('keydown', handler)

    return () => {
      target.removeEventListener('keydown', handler)
    }
  }, [enabled, target, handleKeyDown])
}

/**
 * Hook to get a formatted string representation of a keyboard shortcut
 * 
 * @example
 * ```tsx
 * const shortcut = formatShortcut({ key: 'k', modifiers: ['ctrl'] })
 * // Returns: "Ctrl+K"
 * ```
 */
export function formatShortcut(shortcut: Pick<KeyboardShortcut, 'key' | 'modifiers'>): string {
  const { key, modifiers = [] } = shortcut
  
  const parts: string[] = []
  
  if (modifiers.includes('ctrl')) {
    parts.push('Ctrl')
  }
  if (modifiers.includes('meta')) {
    parts.push('⌘')
  }
  if (modifiers.includes('alt')) {
    parts.push('Alt')
  }
  if (modifiers.includes('shift')) {
    parts.push('Shift')
  }
  
  // Format the key
  let keyDisplay = key
  if (key === ' ') {
    keyDisplay = 'Space'
  } else if (key.length === 1) {
    keyDisplay = key.toUpperCase()
  } else {
    // Handle special keys
    keyDisplay = key
      .replace('Arrow', '')
      .replace('Enter', '↵')
      .replace('Escape', 'Esc')
      .replace('Backspace', '⌫')
      .replace('Delete', '⌦')
      .replace('Tab', '⇥')
  }
  
  parts.push(keyDisplay)
  
  return parts.join('+')
}

export default useKeyboardShortcuts

